import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { orgUnitTypesApi } from '../organizationApi';
import type { MoveLevelMode, OrgUnitTypeListItem, OrgUnitTypeOption } from '../organizationTypes';

/**
 * Move a tier to another level.
 *
 * **A level cannot be typed in.** Setting one tier's position says nothing about what happens
 * to the tier already there, so every change is expressed against an existing level — swap
 * with it, or insert at it. Those are the only two answers to that question, and both leave
 * the ladder contiguous, which is what makes a level a position rather than a number someone
 * chose.
 */
export function MoveTierLevelModal({
  open,
  tier,
  onClose,
  onMoved,
}: {
  open: boolean;
  tier: OrgUnitTypeListItem;
  onClose: () => void;
  onMoved: () => void;
}) {
  const [level, setLevel] = useState<string | null>(null);
  const [mode, setMode] = useState<MoveLevelMode>('swap');
  const [ladder, setLadder] = useState<OrgUnitTypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    orgUnitTypesApi
      .options(controller.signal)
      .then((loaded) => setLadder([...loaded].sort((a, b) => a.sortOrder - b.sortOrder)))
      .catch((caught) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        const failure = caught instanceof ApiError ? caught : new ApiError('Could not load the ladder.', 0);

      setError(failure);
      // No focus call: a failed load names no field, so there is nothing to point at.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  /*
    Every level except this tier's own. Offering it would be a move to where it already is,
    which the server refuses — a control that offers a choice it will then reject is a trap.
  */
  const options = ladder
    .map((entry, index) => ({ entry, level: index + 1 }))
    .filter(({ entry }) => entry.id !== tier.id)
    .map(({ entry, level: position }) => ({
      value: String(position),
      label: entry.name,
      right: position,
    }));

  const target = level === null ? null : Number(level);
  const occupant = target === null ? null : ladder[target - 1];
  const canSave = target !== null && confirmed && !saving;

  async function save() {
    if (!canSave || target === null) return;

    setSaving(true);
    setError(null);

    try {
      await orgUnitTypesApi.moveLevel(tier.id, { level: target, mode });
      onMoved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not move the tier.', 0);

      setError(failure);

      // Say which field, show it, and — on a stepped form — go to its step.
      focusValidationError(failure, {
        idPrefix: 'tier-move-level',
        // The server calls it the level; the control is the target to move to.
        fieldIds: { level: 'tier-move-level-target' },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Move tier level"
      size="lg"
      id="tier-move-level-modal"
      /*
        Picks from the levels the form itself offers — `options` already excludes this tier's
        own position, which the server refuses.

        The mode is left at its default. Swap and insert move different numbers of rows and the
        form states each consequence before the tick; choosing between them is the reader's,
        and a generator that picked one would be answering the question the modal exists to ask.
      */
      autogenerate={{
        onGenerate: () => {
          if (options.length > 0) setLevel(options[Math.floor(Math.random() * options.length)].value);
        },
        disabled: saving || options.length === 0,
      }}
      confirm={{
        id: 'tier-move-level-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="tier-move-level-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            id="tier-move-level-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <p className="text-sm text-slate-600">
          <span className="font-medium text-slate-800">{tier.name}</span> is at level{' '}
          <span className="font-mono tabular-nums text-slate-800">{tier.level}</span>. Moving it
          changes which column every unit at these tiers draws in.
        </p>

        <Select
          id="tier-move-level-target"
          label="Move to level"
          required
          remote
          loading={loading}
          description="An existing level, named by the tier that holds it. A level of its own would leave a gap or a duplicate in the ladder."
          value={level}
          onChange={setLevel}
          options={options}
          // Already in ladder order — sorting again would scramble it.
          sort="none"
          error={target === null ? 'Choose a level.' : undefined}
          disabled={saving}
        />

        {/*
          The consequence is stated per option, before the tick — each one says exactly what
          happens to the other tiers, which is the whole difference between them.
        */}
        <RadioGroup
          id="tier-move-level-mode"
          label="How"
          value={mode}
          onChange={(next) => setMode(next as MoveLevelMode)}
          options={[
            {
              value: 'swap',
              label: 'Swap',
              description: occupant
                ? `${tier.name} and ${occupant.name} trade places. No other tier moves.`
                : 'The two tiers trade places. No other tier moves.',
            },
            {
              value: 'insert',
              label: 'Insert',
              description: occupant
                ? `${tier.name} takes level ${target}, and ${occupant.name} and everything below it shift down one.`
                : 'This tier takes the level, and the tiers from there down shift by one.',
            },
          ]}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
